import { useGetList } from "ra-core";
import { Link } from "react-router-dom";
import {
  Users,
  Crown,
  Target,
  Zap,
  GraduationCap,
  Sparkles,
  ExternalLink,
  Mail,
  Linkedin,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar } from "../contacts/Avatar";
import { RecordContextProvider } from "ra-core";
import type { Contact } from "../types";

export const CompanyStakeholderMatrix = ({ companyId }: { companyId: number }) => {
  const { data: contacts, isPending } = useGetList<Contact>("contacts_summary", {
    filter: { company_id: companyId },
    pagination: { page: 1, perPage: 100 },
    sort: { field: "last_name", order: "ASC" },
  });

  if (isPending) {
    return (
      <div className="p-8 text-center text-xs text-muted-foreground animate-pulse">
        Loading account stakeholder matrix...
      </div>
    );
  }

  if (!contacts || contacts.length === 0) {
    return (
      <div className="p-8 text-center text-xs text-muted-foreground border border-dashed rounded-xl my-3">
        No contacts linked to this account yet. Add key decision makers to build your stakeholder map.
      </div>
    );
  }

  // Group contacts by role
  const sponsors = contacts.filter((c: any) => c.stakeholder_role === "sponsor");
  const decisionMakers = contacts.filter((c: any) => c.stakeholder_role === "decision_maker");
  const champions = contacts.filter((c: any) => c.stakeholder_role === "champion" || !c.stakeholder_role);
  const participants = contacts.filter((c: any) => c.stakeholder_role === "participant");
  const others = contacts.filter((c: any) => c.stakeholder_role === "influencer");

  return (
    <div className="space-y-4 pt-2">
      <div className="p-3 rounded-xl bg-muted/30 border border-border/70 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs">
        <div>
          <span className="font-bold text-foreground flex items-center gap-1.5">
            <Users className="w-4 h-4 text-primary" />
            Account Stakeholder & Penetration Matrix
          </span>
          <span className="text-muted-foreground block text-[11px] mt-0.5">
            Mapping executive sponsors, decision-makers, and cohort participants across {contacts.length} registered contacts
          </span>
        </div>
        <Badge variant="outline" className="text-xs bg-background">
          {sponsors.length} Sponsors • {decisionMakers.length} Decision Makers • {champions.length} Champions
        </Badge>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {/* Tier 1: Executive Sponsors */}
        {sponsors.length > 0 && (
          <RoleTierSection
            title="Executive Sponsors & Board Allies"
            subtitle="C-Suite budget signatories & enterprise transformation sponsors"
            icon={Crown}
            iconBg="bg-amber-500/15 text-amber-600 dark:text-amber-400"
            badge="Budget Sign-Off"
            contacts={sponsors}
          />
        )}

        {/* Tier 2: Decision Makers */}
        {decisionMakers.length > 0 && (
          <RoleTierSection
            title="Strategic Decision Makers"
            subtitle="Business unit leaders, Heads of People, and Capability directors"
            icon={Target}
            iconBg="bg-rose-500/15 text-rose-600 dark:text-rose-400"
            badge="Evaluation & Scope"
            contacts={decisionMakers}
          />
        )}

        {/* Tier 3: Internal Champions */}
        {champions.length > 0 && (
          <RoleTierSection
            title="Internal Champions & Program Leads"
            subtitle="Key champions driving day-to-day engagement, rollout, and advocacy"
            icon={Zap}
            iconBg="bg-indigo-500/15 text-indigo-600 dark:text-indigo-400"
            badge="Advocacy & Ops"
            contacts={champions}
          />
        )}

        {/* Tier 4: Cohort Learners / Coaches */}
        {participants.length > 0 && (
          <RoleTierSection
            title="Cohort Learners & Accredited Coaches"
            subtitle="Participants in Five Lens accreditation or LearnSmartly capability cohorts"
            icon={GraduationCap}
            iconBg="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
            badge="Program Cohort"
            contacts={participants}
          />
        )}

        {/* Tier 5: Influencers */}
        {others.length > 0 && (
          <RoleTierSection
            title="Account Influencers & Team Members"
            subtitle="Cross-functional partners and advisory collaborators"
            icon={Sparkles}
            iconBg="bg-purple-500/15 text-purple-600 dark:text-purple-400"
            badge="Team & Network"
            contacts={others}
          />
        )}
      </div>
    </div>
  );
};

const RoleTierSection = ({
  title,
  subtitle,
  icon: Icon,
  iconBg,
  badge,
  contacts,
}: {
  title: string;
  subtitle: string;
  icon: any;
  iconBg: string;
  badge: string;
  contacts: any[];
}) => (
  <div className="p-3.5 rounded-xl border border-border/80 bg-card space-y-2.5">
    <div className="flex items-center justify-between gap-2 border-b border-border/60 pb-2">
      <div className="flex items-center gap-2">
        <div className={`p-1.5 rounded-lg ${iconBg}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <h4 className="font-bold text-xs text-foreground tracking-tight">{title}</h4>
          <span className="text-[10px] text-muted-foreground block">{subtitle}</span>
        </div>
      </div>
      <Badge variant="secondary" className="text-[10px] py-0 px-2 h-4">
        {badge} ({contacts.length})
      </Badge>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {contacts.map((contact) => {
        const profile = contact.five_lens_profile;
        const email = contact.email_jsonb?.[0]?.email;
        return (
          <RecordContextProvider key={contact.id} value={contact}>
            <div className="p-2.5 rounded-lg border border-border/60 bg-muted/20 hover:bg-muted/40 transition-colors flex items-center justify-between gap-2.5">
              <div className="flex items-center gap-2.5 min-w-0">
                <Avatar />
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-bold text-xs text-foreground truncate">
                      {contact.first_name} {contact.last_name}
                    </span>
                    {profile?.core_type && (
                      <Badge className="bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30 text-[9px] py-0 px-1 font-mono">
                        Type {profile.core_type}{profile.wing ? `w${profile.wing}` : ""}
                      </Badge>
                    )}
                  </div>
                  <span className="text-[11px] text-muted-foreground block truncate">
                    {contact.title || "Executive"}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-1 shrink-0">
                {email && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-foreground"
                    title={email}
                    onClick={() => (window.location.href = `mailto:${email}`)}
                  >
                    <Mail className="w-3.5 h-3.5" />
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 text-[11px] gap-1 shadow-2xs"
                  render={
                    <Link to={`/contacts/${contact.id}/show`}>
                      <span>Dossier</span>
                      <ExternalLink className="w-3 h-3" />
                    </Link>
                  }
                />
              </div>
            </div>
          </RecordContextProvider>
        );
      })}
    </div>
  </div>
);
